import { useAtom } from "jotai"
import { useCallback, useEffect, useRef } from "react"
import type { RefObject } from "react"
import { pendingAudioByProfileIdAtom } from "../store"
import type { API } from "@/api/client"
import { useAuth } from "@/shared/lib/auth-context"
import { appPath } from "@/shared/lib/appPath"
import { formatDateShort, formatTimeHm } from "@/shared/lib/format"
import type { LocalAudioRecord } from "../types"
import { formatMsToMmSs } from "../utils"
import {
    DEMO_COPILOT_SUMMARY,
    DEMO_DIALOG_AUDIO_PATH,
    DEMO_SUMMARY_DELAY_MS,
} from "../tour/demoPlayback"
import { useAudioRecordMutations } from "./useAudioRecordMutations"
import { usePendingAudioActions } from "./usePendingAudioActions"
import { useRecordingMachineBridge } from "./useRecordingMachineBridge"

/**
 * Public audio-recordings API. Recording/upload control flow lives in
 * recordingMachine via useRecordingMachineBridge; this file is the adapter shell.
 */
export function useAudioRecordings(
    api: API<unknown>,
    selectedProfileId: string,
    copilotSessionIdRef?: RefObject<string | null>,
) {
    const auth = useAuth()
    const [localAudioByProfileId, setLocalAudioByProfileId] = useAtom(pendingAudioByProfileIdAtom)
    const { renameRecord, deleteRecord } = useAudioRecordMutations()
    const localAudioByProfileIdRef = useRef(localAudioByProfileId)
    useEffect(() => {
        localAudioByProfileIdRef.current = localAudioByProfileId
    }, [localAudioByProfileId])

    const updateLocalRecord = useCallback(
        (localId: string, patch: (r: LocalAudioRecord) => LocalAudioRecord) => {
            setLocalAudioByProfileId((prev) => {
                const updated: Record<string, LocalAudioRecord[]> = {}
                for (const [profileId, records] of Object.entries(prev)) {
                    updated[profileId] = records.map((r) => (r.id === localId ? patch(r) : r))
                }
                return updated
            })
        },
        [setLocalAudioByProfileId],
    )

    const ownerProfileIdOf = useCallback((localId: string): string | undefined => {
        return Object.entries(localAudioByProfileIdRef.current).find(([, list]) =>
            list.some((r) => r.id === localId),
        )?.[0]
    }, [])

    const bridge = useRecordingMachineBridge({
        api,
        selectedProfileId,
        copilotSessionIdRef,
        username: auth.user?.username,
        localAudioByProfileIdRef,
        setLocalAudioByProfileId,
        updateLocalRecord,
        ownerProfileIdOf,
    })

    const {
        renameLocalAudioRecord,
        deleteLocalAudioRecord: deletePendingRecord,
        setFinalizingRecord,
        attachCopilotSummary,
        markSummaryFailed,
    } = usePendingAudioActions({
        username: auth.user?.username,
        getList: useCallback(
            (profileId: string) => localAudioByProfileIdRef.current[profileId] ?? [],
            [],
        ),
        setLocalAudioByProfileId,
        renameRecord,
        deleteRecord,
    })

    const demoSummaryTimeoutsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())

    const clearDemoSummaryTimeout = useCallback((localId: string) => {
        const timeoutId = demoSummaryTimeoutsRef.current.get(localId)
        if (timeoutId == null) return
        window.clearTimeout(timeoutId)
        demoSummaryTimeoutsRef.current.delete(localId)
    }, [])

    const addLocalDemoRecord = useCallback(
        ({
            durationSec,
            profileId,
        }: {
            durationSec: number
            /** Prefer meeting profile when Stop happens on another profile. */
            profileId?: string
        }): string => {
            const targetId = profileId || selectedProfileId
            if (!targetId) return ""
            const now = new Date()
            const localId = `${now.getTime()}`
            const copilotSessionId = `demo-${localId}`
            const safeDuration = Number.isFinite(durationSec) && durationSec > 0 ? durationSec : 0
            setLocalAudioByProfileId((prev) => ({
                ...prev,
                [targetId]: [
                    ...(prev[targetId] ?? []),
                    {
                        id: localId,
                        copilotSessionId,
                        name: "Новая аудиозапись",
                        url: appPath(DEMO_DIALOG_AUDIO_PATH),
                        blob: new Blob(),
                        durationSec: safeDuration || undefined,
                        duration: formatMsToMmSs(safeDuration * 1000),
                        date: formatDateShort(now),
                        time: formatTimeHm(now),
                        createdAt: now.getTime(),
                        isUploading: false,
                        isFinalizing: true,
                    },
                ],
            }))
            const timeoutId = window.setTimeout(() => {
                demoSummaryTimeoutsRef.current.delete(localId)
                attachCopilotSummary(copilotSessionId, DEMO_COPILOT_SUMMARY)
            }, DEMO_SUMMARY_DELAY_MS)
            demoSummaryTimeoutsRef.current.set(localId, timeoutId)
            return localId
        },
        [attachCopilotSummary, selectedProfileId, setLocalAudioByProfileId],
    )

    const deleteLocalAudioRecord = useCallback(
        async (args: { profileId: string; localId: string }) => {
            clearDemoSummaryTimeout(args.localId)
            await deletePendingRecord(args)
        },
        [clearDemoSummaryTimeout, deletePendingRecord],
    )

    useEffect(() => {
        const demoSummaryTimeouts = demoSummaryTimeoutsRef.current
        const localAudioByProfileId = localAudioByProfileIdRef
        return () => {
            for (const timeoutId of demoSummaryTimeouts.values()) {
                window.clearTimeout(timeoutId)
            }
            demoSummaryTimeouts.clear()
            for (const list of Object.values(localAudioByProfileId.current)) {
                for (const r of list) {
                    try {
                        URL.revokeObjectURL(r.url)
                    } catch {
                        void 0
                    }
                }
            }
        }
    }, [])

    return {
        localAudioByProfileId,
        renameLocalAudioRecord,
        deleteLocalAudioRecord,
        setFinalizingRecord,
        attachCopilotSummary,
        markSummaryFailed,
        addLocalDemoRecord,
        ...bridge,
    }
}
